import { fhirClient } from 'src/App';
import { JASPER_FUNCTION_URL, RENDER_TEMPLATE_BACK_URL } from 'src/config';
import { apiClient } from 'src/careflow/ph-core/api_client/api_client';

export default function usePrintableCarePlan(configurations: any) {
  async function getRender(payload): Promise<any> {
    try {
      const response = await apiClient.post(
        RENDER_TEMPLATE_BACK_URL(configurations?.printableCarePlan?.renderTemplate), 
        payload
      );
      return response;
    } catch (error) {
      console.error(error);
    }
  }
  async function getJasperRender(payload): Promise<any> {
    try {
      const response = await apiClient.post(
        JASPER_FUNCTION_URL(configurations?.printableCarePlan?.jasperReport), 
        payload,
       { responseType: 'blob' }
      );
      return response;
    } catch (error) {
      console.error(error);
    }
  }
  
  async function getCarePlanData(carePlanId: string, includeFullEntry: boolean): Promise<any[]> {
    let query = `/CarePlan?_id=${carePlanId}`;
    let carePlanQueryRes;

    try {
      carePlanQueryRes = await fhirClient.search(query);
    } catch (error) {
      console.error(error);
      throw new Error('failed to get CarePlan data for printable CarePlan');
    }

    if (carePlanQueryRes.entry.length < 1) {
      throw new Error('failed to get CarePlan data');
    }

    let patientId: string = '';
    const resourcesList: any[] = [];

    carePlanQueryRes.entry.forEach((item: any) => {
      if (item.resource) {
        resourcesList.push(includeFullEntry?item:item.resource);
      }

      if (
        item.resource.resourceType === 'CarePlan' &&
        item.resource.subject &&
        item.resource.subject.reference &&
        item.resource.subject.reference.includes('Patient/')
      ) {
        patientId = item.resource.subject.reference.substring(item.resource.subject.reference.indexOf('/') + 1);
      }
    });

    if (patientId === '') {
      throw new Error('failed to get Patient id from CarePlan data');
    }

    let queryPatient = `/Patient?_id=${patientId}`;
    queryPatient += '&_revinclude=CareTeam:subject';
    queryPatient += '&_revinclude=Goal:subject';
    queryPatient += '&_revinclude=MeasureReport:subject';
    queryPatient += '&_revinclude=RelatedPerson:patient';
    queryPatient += '&_revinclude=Observation:subject';
    queryPatient += '&_revinclude=Task:subject';
    queryPatient += '&_revinclude=Communication:subject';

    let patientQueryRes;

    try {
      patientQueryRes = await fhirClient.search(queryPatient);
    } catch (error) {
      console.error(error);
      throw new Error('failed to get Patient data for printable CarePlan');
    }

    const practitionersIdsList: string[] = [];

    if (!patientQueryRes || !patientQueryRes.entry) {
      return resourcesList;
    }

    const latestCommunicationTopic = {};

    patientQueryRes.entry.forEach((item: any) => {
      if (item.resource.resourceType !== 'Communication') {
        resourcesList.push(includeFullEntry?item:item.resource);
      }

      if(item.resource.resourceType === 'Communication'){
        const topic = item.resource.topic?.coding?.[0]?.code;
        const sentDate = new Date(item.resource.sent);

        if (
          configurations?.printableCarePlan?.filterCommunicationTopics &&
          !configurations.printableCarePlan.filterCommunicationTopics.includes(topic)
        ) {
          return;
        }

        if (!latestCommunicationTopic[topic] || sentDate > latestCommunicationTopic[topic].sentDate) {
          latestCommunicationTopic[topic] = { item, sentDate };
        }
      }

      if (item.resource.resourceType === 'CareTeam' && item.resource.participant) {
        item.resource.participant.forEach((item2: any) => {
          if (item2.member && item2.member.reference) {
            if(item2.member.reference.startsWith('Practitioner')){
              const pId = item2.member.reference.substring(item2.member.reference.indexOf('/') + 1);
              practitionersIdsList.push(pId);
            }
          }
        });
      }
    });

    Object.values(latestCommunicationTopic).forEach((item: any) => {
      resourcesList.push(includeFullEntry ? item?.item : item?.item?.resource);
    });

    if (practitionersIdsList.length > 0) {
      let queryPract = `/Practitioner?_id=${practitionersIdsList.join(',')}`;

      let practitionerQueryRes;

      try {
        practitionerQueryRes = await fhirClient.search(queryPract);
      } catch (error) {
        console.error(error);
        throw new Error('failed to get Practitioners data for printable CarePlan');
      }

      practitionerQueryRes.entry?.forEach((item: any) => {
        if (item.resource) {
          resourcesList.push(includeFullEntry?item:item.resource);
        }
      });
    }
    return resourcesList;
  }

  async function getPrintableCareplan(carePlanId: string) {
    const resp = await getCarePlanData(carePlanId,false);

    if (!resp) {
      return;
    }

    // TODO: REMOVE process when template is fixed
    const rList = JSON.stringify(resp);

    const requestData = {
      template_url: configurations?.printableCarePlan?.templateUrl,
      // mo_url:
      //   'https://functions.patientholistics.app/survey_templates/messages_ehn_therapy_management.mo',
      data: {
        data: rList,
      },
    };

    const render = await getRender(requestData);

    return render;
  }

  async function getJasperPrintableCareplan(carePlanId: string): Promise<Blob | undefined> {
    const resp = await getCarePlanData(carePlanId,true);

    if (!resp) {
      return;
    }

    // TODO: REMOVE process when template is fixed
    //const rList = JSON.stringify(resp);


    const requestData : any[] = [];
    configurations?.printableCarePlan?.jasperFilesUrl?.split(',').forEach((url)=>{
      requestData.push({"report_url":url});
    })
    requestData[0].type='main';
    requestData[0].json_data={"entry":resp};

    const render = await getJasperRender(requestData);
    
    return render;
  }

  return { getPrintableCareplan, getJasperPrintableCareplan };
}
